import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Activity, Brain, Handshake, Sparkles, Wallet } from 'lucide-react';

interface DimensionData {
  current: number;
  previous: number;
  history: number[];
}

interface DimensionPulseCardProps {
  dimension: 'salute' | 'focus' | 'connessione' | 'spirito' | 'finanza';
  data: DimensionData;
  label: string;
  noDataLabel?: string;
  trendLabel?: string;
  delay?: number;
}

const dimensionConfig = {
  salute: {
    icon: Activity,
    gradient: 'from-rose-500 to-pink-600',
    bgLight: 'bg-rose-50',
    textColor: 'text-rose-600',
    sparklineColor: '#f43f5e'
  },
  focus: {
    icon: Brain,
    gradient: 'from-amber-500 to-orange-600',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-600',
    sparklineColor: '#f59e0b'
  },
  connessione: {
    icon: Handshake,
    gradient: 'from-blue-500 to-indigo-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    sparklineColor: '#3b82f6'
  },
  spirito: {
    icon: Sparkles,
    gradient: 'from-purple-500 to-violet-600',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-600',
    sparklineColor: '#8b5cf6'
  },
  finanza: {
    icon: Wallet,
    gradient: 'from-emerald-500 to-teal-600',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    sparklineColor: '#10b981'
  }
};

const Sparkline: React.FC<{ data: number[]; color: string; width?: number; height?: number }> = ({
  data,
  color,
  width = 60,
  height = 24
}) => {
  const filteredData = data.filter(d => d > 0);
  if (filteredData.length < 2) return null;

  const min = Math.min(...filteredData);
  const max = Math.max(...filteredData);
  const range = max - min || 1;
  
  const points = filteredData.map((value, index) => {
    const x = (index / (filteredData.length - 1)) * width;
    const y = height - ((value - min) / range) * height * 0.8 - height * 0.1;
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `0,${height} ${points} ${width},${height}`;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`sparkline-gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#sparkline-gradient-${color})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={width}
        cy={height - ((filteredData[filteredData.length - 1] - min) / range) * height * 0.8 - height * 0.1}
        r="2"
        fill={color}
      />
    </svg>
  );
};

const DimensionPulseCard: React.FC<DimensionPulseCardProps> = ({
  dimension,
  data,
  label,
  noDataLabel = 'No data',
  trendLabel = '7d',
  delay = 0
}) => {
  const config = dimensionConfig[dimension];
  const Icon = config.icon;

  const trend = useMemo(() => {
    if (data.current === 0 && data.previous === 0) {
      return { direction: 'stable', value: 0, color: 'text-gray-300' };
    }
    
    const diff = data.current - data.previous;
    const percentChange = data.previous !== 0 ? Math.round((diff / Math.abs(data.previous)) * 100) : (data.current > 0 ? 100 : 0);
    
    if (diff > 3) return { direction: 'up', value: percentChange, color: 'text-emerald-500' };
    if (diff < -3) return { direction: 'down', value: percentChange, color: 'text-rose-500' };
    return { direction: 'stable', value: 0, color: 'text-gray-400' };
  }, [data]);

  const TrendIcon = trend.direction === 'up' ? TrendingUp : trend.direction === 'down' ? TrendingDown : Minus;
  const hasData = data.current > 0 || data.history.some(h => h > 0);

  return (
    <div 
      className="group relative bg-white rounded-[20px] p-4 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-9 h-9 rounded-xl ${config.bgLight} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
            <Icon className={`w-5 h-5 ${config.textColor}`} />
          </div>
          
          {hasData && trend.value !== 0 && (
            <div className="flex flex-col items-end gap-0">
              <div className="flex items-center gap-0.5">
                <TrendIcon className={`w-3 h-3 ${trend.color}`} />
                <span className={`text-[10px] font-bold ${trend.color}`}>
                  {trend.value > 0 ? '+' : ''}{trend.value}%
                </span>
              </div>
              <span className="text-[8px] text-black/30 font-medium">{trendLabel}</span>
            </div>
          )}
        </div>

        <p className="text-[10px] font-bold text-black/40 uppercase tracking-wider mb-1 truncate">{label}</p>
        
        <div className="flex items-end justify-between gap-2">
          {hasData ? (
            <>
              <p className="text-[24px] font-black text-black leading-none">
                {data.current}
                <span className="text-[12px] font-bold text-black/30">%</span>
              </p>
              {data.history.some(h => h > 0) && (
                <Sparkline data={data.history} color={config.sparklineColor} width={50} height={20} />
              )}
            </>
          ) : (
            <p className="text-[12px] font-medium text-black/30 py-1">
              {noDataLabel}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DimensionPulseCard;
